import { type ComponentProps, useCallback, useState } from 'react';
import styled from 'styled-components';

import { isSortable } from '@dnd-kit/dom/sortable';
import { DragDropProvider } from '@dnd-kit/react';

import Foldout from 'components/shared/Foldout';
import SectionHeader from 'components/shared/form/SectionHeader';
import { colors } from 'styles/theme/colors';

import CategoryCard from './CategoryCard';
import CriteriaSlideout from './CriteriaSlideout';
import type { CriteriaCategory } from './NewCategoryModal';
import NewCategoryModal from './NewCategoryModal';

export type CriteriaItem = {
  id?: string;
  _tempId?: string;
  criterion: string;
  description: string;
  category: string;
  order: number;
};

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>;

type Props = {
  categories: CriteriaCategory[];
  criteria: CriteriaItem[];
  onCategoriesChange: (categories: CriteriaCategory[]) => void;
  onCriteriaChange: (criteria: CriteriaItem[]) => void;
};

export function generateTempCategoryId() {
  return `_temp_cat_${crypto.randomUUID()}`;
}

export function generateTempCriteriaId() {
  return `_temp_crit_${crypto.randomUUID()}`;
}

export default function CategoriesList({ categories, criteria, onCategoriesChange, onCriteriaChange }: Props) {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [slideoutOpen, setSlideoutOpen] = useState(false);

  const selectedCategory = selectedCategoryId
    ? (categories.find((c) => c._tempId === selectedCategoryId) ?? null)
    : null;

  const handleDragEnd = useCallback(
    (...[event]: Parameters<DragEndHandler>) => {
      if (event.canceled) return;
      const { source } = event.operation;

      if (!source || !isSortable(source)) return;

      const oldIndex = source.initialIndex;
      const newIndex = source.index;

      if (oldIndex === newIndex) return;

      const reordered = [...categories];
      const [moved] = reordered.splice(oldIndex, 1);

      reordered.splice(newIndex, 0, moved);

      onCategoriesChange(reordered.map((cat, i) => ({ ...cat, order: i })));
    },
    [categories, onCategoriesChange]
  );

  const handleAddCategory = (category: Pick<CriteriaCategory, 'name' | 'color'>) => {
    const newCategory: CriteriaCategory = {
      _tempId: generateTempCategoryId(),
      ...category,
      order: categories.length,
    };

    onCategoriesChange([...categories, newCategory]);
    setShowCategoryModal(false);
  };

  const handleEditCategory = (updates: Pick<CriteriaCategory, 'name' | 'color'>) => {
    if (!selectedCategory) return;

    const updatedCategories = categories.map((c) =>
      c._tempId === selectedCategory._tempId ? { ...c, name: updates.name, color: updates.color } : c
    );

    onCategoriesChange(updatedCategories);
  };

  const handleDeleteCategory = (cat: CriteriaCategory) => {
    const updatedCategories = categories.filter((c) => c._tempId !== cat._tempId).map((c, i) => ({ ...c, order: i }));
    const updatedCriteria = criteria.filter((c) => c.category !== cat.name);

    onCategoriesChange(updatedCategories);
    onCriteriaChange(updatedCriteria);
    setSlideoutOpen(false);
    setSelectedCategoryId(null);
  };

  const handleCategoryClick = (cat: CriteriaCategory) => {
    setSelectedCategoryId(cat._tempId);
    setSlideoutOpen(true);
  };

  const handleApplyCriteria = (updatedCriteria: CriteriaItem[]) => {
    if (!selectedCategory) return;

    // Replace criteria for this category, keeping other categories intact
    const otherCriteria = criteria.filter((c) => c.category !== selectedCategory.name);

    onCriteriaChange([...otherCriteria, ...updatedCriteria]);
  };

  const getCriteriaCount = (categoryName: string) => criteria.filter((c) => c.category === categoryName).length;

  const getCriteriaForCategory = (categoryName: string) => criteria.filter((c) => c.category === categoryName);

  return (
    <>
      <HeaderRow>
        <SectionHeader>Criteria Categories</SectionHeader>
        <AddLink onClick={() => setShowCategoryModal(true)}>+ Add New Category</AddLink>
      </HeaderRow>

      {categories.length > 0 && (
        <DragDropProvider onDragEnd={handleDragEnd}>
          <CardList>
            {categories.map((cat, index) => {
              const count = getCriteriaCount(cat.name);

              return (
                <CategoryCard
                  key={cat._tempId}
                  category={cat}
                  index={index}
                  criteriaCount={count}
                  onClick={() => handleCategoryClick(cat)}
                />
              );
            })}
          </CardList>
        </DragDropProvider>
      )}

      <NewCategoryModal
        show={showCategoryModal}
        onCancel={() => setShowCategoryModal(false)}
        onSubmit={handleAddCategory}
        existingNames={categories.map((c) => c.name)}
      />

      <Foldout open={slideoutOpen} setOpen={setSlideoutOpen} closeOnBackdropClick width='min(85%, 917px)'>
        {selectedCategory && (
          <CriteriaSlideout
            key={selectedCategory._tempId}
            handleClose={() => setSlideoutOpen(false)}
            category={selectedCategory}
            criteria={getCriteriaForCategory(selectedCategory.name)}
            allCategories={categories}
            onEditCategory={handleEditCategory}
            onDeleteCategory={() => handleDeleteCategory(selectedCategory)}
            onApply={handleApplyCriteria}
          />
        )}
      </Foldout>
    </>
  );
}

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > *:first-child {
    margin-bottom: 0;
  }
`;

const AddLink = styled.span`
  color: ${colors.primaryBlue};
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;
